import React, { FC, ReactNode } from 'react'
import { makeStyles, Theme } from '@material-ui/core/styles'
import {
  Button,
  Checkbox,
  Link,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
} from '@material-ui/core'
import { ApolloError } from 'apollo-boost'
import { Order } from '@src/types/graphql-types'
import {
  OrderDetailQuery,
  useOrderDetailQuery,
} from '@src/queries/OrderDetailsQuery.generated'
import ModalSkeleton from './ModalSkeleton'
import LoadingBox from '@src/components/LoadingBox'
import ListError from '@src/components/ListError'
import HandleMarkup from '@src/components/HandleMarkup'
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl'
import messages from '@utils/messages'
import { orderCenterRoutes } from '@src/routes'
import { useQuickOrder } from '@src/components/QuickOrder/utils/quickOrderContext'
import { MODALTYPE } from './enum'
import { sendCommonDetailClickEvent } from '@src/utils/analytics'
import {
  DejaNewEventCategory as eventCategory,
  DejaNewEventAction as eventAction,
  DejaNewEventLabel as eventLabel,
} from '@utils/analytics/enums'
//TODO: consider breaking into different files

const useStyles = makeStyles((theme: Theme) => {
  return {
    checkbox: {
      padding: '0 0 0.2rem 0',
      marginRight: '.5rem',
    },
    loadingBox: {
      backgroundImage:
        'linear-gradient(to right, #cfcfcf, #e0e0e0 25%, #f9f9f9, #e0e0e0 75%, #cfcfcf)',
      animationDuration: '2s',
      animationDelay: '0s',
      animationTimeFunction: 'ease-in',
    },
    orderDetailsLink: { paddingLeft: '.2rem' },
    skuCell: {
      alignItems: 'center',
      paddingLeft: '0px !important',
      whiteSpace: 'nowrap',
    },
    tableContainer: {
      borderBottom: '1px solid rgba(224, 224, 224, 1)',
      maxHeight: '14rem',
      marginBottom: '.5rem',
    },
    tableHeadCell: {
      paddingLeft: '1.7rem !important',
      width: '10rem',
    },
    tableHeadRow: {
      whiteSpace: 'nowrap',
      fontSize: 14,
      '& .MuiTableCell-head': {
        padding: '.2rem .6rem',
        fontWeight: `${theme.typography.fontWeightMedium} !important`,
      },
    },
    tableRow: {
      '& .MuiTableCell-body': {
        border: 0,
      },
    },
  }
})

const RecentOrdersModalTitle: FC<{ order: Order }> = ({ order }) => {
  const intl = useIntl()

  return (
    <>
      {intl.formatMessage(messages.ORDER_MODAL_TITLE, {
        orderNumber: order.salesDocNumber,
      })}
      <FormattedDate
        value={new Date(order.orderDate)}
        year="numeric"
        month="short"
        day="numeric"
      />
    </>
  )
}

const ItemRow: FC<{
  index: number
  name: string
  number: string
  quantity: number
}> = ({ index, name, number, quantity }) => {
  const classes = useStyles()
  const { pendingItems, updatePendingItems } = useQuickOrder()

  const isChecked = pendingItems.some((item) => item.id === index)

  const handleCheck = () => {
    updatePendingItems(index, number, quantity)
  }

  return (
    <>
      <TableRow classes={{ root: classes.tableRow }}>
        <TableCell className={classes.skuCell}>
          <Checkbox
            classes={{ root: classes.checkbox }}
            checked={isChecked}
            color="primary"
            onChange={() => handleCheck()}
            size="small"
          />
          {number}
        </TableCell>
        <TableCell>{quantity}</TableCell>
        <TableCell>
          <HandleMarkup value={name} />
        </TableCell>
      </TableRow>
    </>
  )
}

const OrderItemsTable: FC<{ children: ReactNode }> = ({ children }) => {
  const classes = useStyles()

  return (
    <>
      <TableContainer className={classes.tableContainer}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow className={classes.tableHeadRow}>
              <TableCell classes={{ root: classes.tableHeadCell }}>
                <FormattedMessage {...messages.SKU} />
              </TableCell>
              <TableCell>
                <FormattedMessage {...messages.ABBREVIATED_QUANTITY} />
              </TableCell>
              <TableCell>
                <FormattedMessage {...messages.NAME} />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{children}</TableBody>
        </Table>
      </TableContainer>
    </>
  )
}

const getAnalyticsModalTitle = (intl, orderNumber, date) => {
  return (
    `${intl.formatMessage(messages.ORDER_MODAL_TITLE, {
      orderNumber: orderNumber,
    })} ${intl.formatDate(new Date(date), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })}` || ''
  )
}

const RecentOrdersModalContent: FC<{
  data: OrderDetailQuery | undefined
  error: ApolloError | undefined
  loading: boolean
  detailTitleForAnalytics: string
}> = ({ data, error, loading, detailTitleForAnalytics }) => {
  const classes = useStyles()

  if (loading)
    return (
      <OrderItemsTable>
        {Array(4)
          .fill({})
          .map((_, index) => (
            <TableRow classes={{ root: classes.tableRow }} key={index}>
              <TableCell className={classes.skuCell}>
                <LoadingBox className={classes.loadingBox} height={24} />
              </TableCell>
              <TableCell>
                <LoadingBox className={classes.loadingBox} height={24} />
              </TableCell>
              <TableCell>
                <LoadingBox className={classes.loadingBox} height={24} />
              </TableCell>
            </TableRow>
          ))}
      </OrderItemsTable>
    )
  if (error || !data || !data.orderDetail) return <ListError />
  return (
    <>
      <OrderItemsTable>
        {data.orderDetail.order.items.map((item, index) => {
          return (
            <ItemRow
              key={index}
              index={index}
              name={(
                item.material?.name ||
                item.productDetails?.name ||
                ''
              ).toUpperCase()}
              number={item.materialNumber}
              quantity={item.quantity}
            />
          )
        })}
      </OrderItemsTable>
      {data.orderDetail.order.orderId && (
        <Link
          className={classes.orderDetailsLink}
          href={`${orderCenterRoutes.orderDetail(
            data.orderDetail.order.orderId
          )}?returnUrl=${encodeURIComponent(orderCenterRoutes.orders())}`}
          onClick={() => {
            const orderIdForTrackingUrl = data.orderDetail.order.orderId || ''

            sendCommonDetailClickEvent(
              {
                event: 'quick_order_widget_interaction',
                action: 'visit order details',
                detail: detailTitleForAnalytics,
                section: 'add to quick order',
                component: 'modal',
                elementType: 'link',
                elementText: 'order details',
                linkUrl: `${orderCenterRoutes.orderDetail(
                  orderIdForTrackingUrl
                )}?returnUrl=${encodeURIComponent(orderCenterRoutes.orders())}`,
                coreEvent: 'no',
              },
              {
                eventCategory: eventCategory.QuickOrderPage,
                eventAction: eventAction.RecentOrders,
                eventLabel: eventLabel.ModalOrderDetails,
                eventInteractionType: 0,
              }
            )
          }}
        >
          <FormattedMessage {...messages.ORDER_DETAILS} />
          {' >'}
        </Link>
      )}
    </>
  )
}

const RecentOrdersModalActions: FC<{
  data: OrderDetailQuery | undefined
  onClose: () => void
  detailTitleForAnalytics: string
}> = ({ data, onClose, detailTitleForAnalytics }) => {
  const { pendingItems, setAddItems, setPendingItems } = useQuickOrder()

  const itemsSelected = pendingItems.length > 0
  const dataLoaded = data && data.orderDetail

  const handleAddSelectedClick = () => {
    sendCommonDetailClickEvent(
      {
        event: 'quick_order_widget_interaction',
        action: 'add selected order items',
        detail: detailTitleForAnalytics,
        section: 'add to quick order',
        component: 'modal',
        elementType: 'button',
        elementText: 'add selected',
        coreEvent: 'no',
      },
      {
        eventCategory: eventCategory.QuickOrderPage,
        eventAction: eventAction.RecentOrders,
        eventLabel: eventLabel.ModalAddSelected,
        eventInteractionType: 0,
      }
    )
    setAddItems(true)
    onClose()
  }

  const handleAddAllClick = () => {
    sendCommonDetailClickEvent(
      {
        event: 'quick_order_widget_interaction',
        action: 'add all order items',
        detail: detailTitleForAnalytics,
        section: 'add to quick order',
        component: 'modal',
        elementType: 'button',
        elementText: 'add all',
        coreEvent: 'no',
      },
      {
        eventCategory: eventCategory.QuickOrderPage,
        eventAction: eventAction.RecentOrders,
        eventLabel: eventLabel.ModalAddAll,
        eventInteractionType: 0,
      }
    )
    setPendingItems(
      data
        ? data.orderDetail.order.items.map((item, index) => {
            return {
              id: index,
              materialNumber: item.materialNumber,
              quantity: item.quantity,
            }
          })
        : []
    )
    setAddItems(true)
    onClose()
  }

  return (
    <>
      <Button
        color="primary"
        disabled={!itemsSelected}
        onClick={() => handleAddSelectedClick()}
        size="small"
        variant="outlined"
      >
        <FormattedMessage {...messages.ADD_SELECTED} />
      </Button>
      <Button
        color="primary"
        disabled={!dataLoaded}
        onClick={() => handleAddAllClick()}
        size="small"
        variant="contained"
      >
        <FormattedMessage {...messages.ADD_ALL} />
      </Button>
    </>
  )
}

const RecentOrdersModal: FC<{
  onClose: () => void
  open: boolean
  order: Order
}> = ({ onClose, open, order }) => {
  const intl = useIntl()
  const orderId = order.orderId ? order.orderId : ''
  const { loading, data, error } = useOrderDetailQuery({
    variables: {
      orderIdentifier: orderId,
      isAcknowledgement: false,
      sysId: order.sysId,
    },
    fetchPolicy: 'cache-and-network',
  })
  const detailTitleForAnalytics = getAnalyticsModalTitle(
    intl,
    order.salesDocNumber,
    order.orderDate
  )
  return (
    <ModalSkeleton
      type={MODALTYPE.OrdersModal}
      actions={
        <RecentOrdersModalActions
          data={data}
          onClose={() => onClose()}
          detailTitleForAnalytics={detailTitleForAnalytics}
        />
      }
      content={
        <RecentOrdersModalContent
          data={data}
          error={error}
          loading={loading}
          detailTitleForAnalytics={detailTitleForAnalytics}
        />
      }
      onClose={() => onClose()}
      open={open}
      title={<RecentOrdersModalTitle order={order} />}
    />
  )
}

export default RecentOrdersModal
